import type { VariantProps } from 'class-variance-authority'
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

const AlertDialog = AlertDialogPrimitive.Root
const AlertDialogTrigger = AlertDialogPrimitive.Trigger

function AlertDialogContent({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Content>) {
    return (
        <AlertDialogPrimitive.Portal>
            <AlertDialogPrimitive.Overlay
                className={cn(
                    'fixed inset-0 z-50 bg-black/40 dark:bg-black/60',
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
                )}
            />
            <AlertDialogPrimitive.Content
                data-slot="alert-dialog-content"
                className={cn(
                    'fixed left-1/2 top-1/2 z-50 grid w-full max-w-md -translate-x-1/2 -translate-y-1/2 gap-4',
                    // Height bound plus a scroller, so a long confirmation never hides its buttons.
                    'max-h-[calc(100dvh-2rem)] overflow-y-auto',
                    'rounded-xl border border-border bg-popover p-5 text-popover-foreground shadow-xl',
                    // A confirmation is a bottom sheet on a phone for the same reason a dialog
                    // is: its buttons belong within thumb reach, not centred on the screen.
                    // See the note in dialog.tsx.
                    'max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:max-w-none',
                    'max-sm:translate-x-0 max-sm:translate-y-0',
                    'max-sm:max-h-[calc(100dvh-3rem)] max-sm:rounded-b-none max-sm:rounded-t-2xl',
                    'max-sm:p-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]',
                    'duration-(--animate-duration)',
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
                    'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
                    'max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom',
                    className
                )}
                {...props}
            />
        </AlertDialogPrimitive.Portal>
    )
}

function AlertDialogHeader({ className, ...props }: ComponentProps<'div'>) {
    return <div data-slot="alert-dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
}

function AlertDialogFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-dialog-footer"
            className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
            {...props}
        />
    )
}

function AlertDialogTitle({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Title>) {
    return (
        <AlertDialogPrimitive.Title
            data-slot="alert-dialog-title"
            className={cn('text-base font-semibold leading-none tracking-tight', className)}
            {...props}
        />
    )
}

function AlertDialogDescription({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Description>) {
    return (
        <AlertDialogPrimitive.Description
            data-slot="alert-dialog-description"
            className={cn('text-sm leading-relaxed text-muted-foreground', className)}
            {...props}
        />
    )
}

function AlertDialogAction({
    className,
    variant,
    ...props
}: ComponentProps<typeof AlertDialogPrimitive.Action> & Pick<VariantProps<typeof buttonVariants>, 'variant'>) {
    return <AlertDialogPrimitive.Action className={cn(buttonVariants({ variant }), className)} {...props} />
}

function AlertDialogCancel({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
    return <AlertDialogPrimitive.Cancel className={cn(buttonVariants({ variant: 'outline' }), className)} {...props} />
}

export {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
}
